import { useCallback, useMemo, useEffect, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import ExperienceGiveAsGiftInfoText from 'components/Experiences/ExperienceGiveAsGiftModal/ExperienceGiveAsGiftInfoText'
import ExperienceInstantConfirmationText from 'components/Experiences/ExperienceTiles/ExperienceInstantConfirmationText'
import { FormattedMessage, useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'
import MobileAndroidIcon from 'LuxKit/Icons/line/LineMobileAndroidIcon'
import ShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import CalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import ChatIcon from 'LuxKit/Icons/line/LineChatIcon'
import ClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'
import { formatExperienceVoucherTypes } from 'lib/experiences/formatExperienceVoucherTypes'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import OfferBNPLAd from 'components/OfferPage/OfferBNPLAd'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import Pane from 'components/Common/Pane'
import useExperienceSearchUrl from 'hooks/Experiences/useExperienceSearchUrl'
import { useAppSelector } from 'hooks/reduxHooks'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import ExperiencePriceDetails from '../ExperiencePriceDetails'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Tooltip from 'LuxKit/Tooltip'
import TextLink from 'LuxKit/TextLink'
import ExperienceAppDiscountTag from '../ExperienceAppDiscountTag'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import PriceRowLuxPlusNonMemberPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusNonMemberPrice'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { trackClientEvent } from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import { isExperienceOfferPageAvailable } from './utils'

const HR = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`
const SoldOutPane = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};

  > * + * {
    margin-top: ${rem(16)};
  }
`

const OverviewContainer = styled(VerticalSpacer)`
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding: ${rem(24)};
`

const TightContainer = styled.div`
  ${mediaQueryUp.tablet} {
    width: ${rem(200)};
  }
`
interface Props {
  experience: App.ExperienceOffer
  onGiveAsGift?: () => void
  luxPlusEnabled: boolean
}

function ExperienceOfferPageOverview(props: Props) {
  const { experience, onGiveAsGift, luxPlusEnabled } = props
  const intl = useIntl()
  const { isActive: isWeddingGiftRegistry } = useWeddingGiftRegistryContext()
  const fromCaption = intl.formatMessage({
    id: 'nBDHIGVvWfzgyt2m8O0C',
    defaultMessage: 'From',
    description: 'Price caption prefix on experience offer page overview',
  })
  const fallbackSaleUnit = intl.formatMessage({
    id: '763MZ8B07SQ3Ex-4eBUO',
    defaultMessage: 'unit',
    description: 'Default sale unit label on experience offer page overview',
  })
  const previewMode = useAppSelector(state => state.system.previewMode)
  const isSpoofAccess = useAppSelector(state => state.auth.account.isSpoofed)
  const durationString = useMemo(() => getExperienceDurationString(experience, intl), [experience, intl])
  const isAvailable = isExperienceOfferPageAvailable({
    experience,
    isSpoofAccess,
    previewMode,
  })

  // complimentary offers have zero price, we should block the booking through the standalone flow without showing the sold out messages
  const hasPrice = experience.price > 0

  const [soldOutSearchUrl] = useExperienceSearchUrl({ latLong: experience.location })
  const experiencesBNBLEnabled = useMemo(() => experience.allowBuyNowBookLater && !isWeddingGiftRegistry, [experience.allowBuyNowBookLater, isWeddingGiftRegistry])
  const isVoucherBased = !experience.hasCalendar
  const trackEvent = useTrackExperienceOfferEvent()

  const showGiftOption = !isWeddingGiftRegistry && (!!experience.giftType && experience.allowGiftCard)

  const showLuxPlusPrice = useMemo(() => {
    return luxPlusEnabled && experience.pricing.memberPrice > 0
  }, [luxPlusEnabled, experience.pricing.memberPrice])

  const handleOnGiveAsGift = useCallback(() => {
    trackEvent('GIVE_AS_GIFT_CLICKED')
    onGiveAsGift?.()
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [onGiveAsGift])

  const handleCheckAvailability = useCallback(() => {
    trackEvent('CHECK_AVAILABILITY_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleBNBLTooltipOpen = useCallback((isOpen: boolean) => {
    if (isOpen) trackEvent('BNBL_LEARN_MORE_BUTTON_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleBrowseAllExperiencesClick = useCallback(() => {
    trackEvent('BROWSE_ALL_EXPERIENCES_CLICK')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const bnblTooltipDescription = intl.formatMessage({
    id: '8CcjqaKiTLE49d_yWf5N',
    defaultMessage: 'Secure an experience and have the flexibility to choose dates at a later time.',
    description: 'Tooltip explaining buy now book later option for experience on PDP',
  })

  const handleInterestFreeLearnMoreClick = useCallback(() => {
    trackEvent('INTEREST_FREE_INFO_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const handleOnSupportEmailClick = useCallback(() => {
    trackEvent('SUPPORT_TEAM_EMAIL_BUTTON_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])
  const handleOnSupportNumberClick = useCallback(() => {
    trackEvent('SUPPORT_TEAM_NUMBER_BUTTON_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [])

  const checkAvailabilityLabel = intl.formatMessage({
    id: 'a868WAlup3_we1KaaRP5',
    defaultMessage: 'Check availability',
    description: 'Button text to check experience availability on PDP sidebar',
  })

  const giveAsGiftLabel = intl.formatMessage({
    id: 'C80t6AKkkQjlLGVqbdjg',
    defaultMessage: 'Give as gift',
    description: 'Button text to give experience as a gift on PDP sidebar',
  })

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorExperienceOptions(experience, {
      ...(showLuxPlusPrice && {
        memberPrice: experience.pricing.memberPrice,
      }),
    }),
  ], [experience, showLuxPlusPrice])

  const shouldTrackPrice = isAvailable && hasPrice && !!experience?.price
  const priceTrackedRef = useRef(false)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()
  useEffect(() => {
    if (shouldTrackPrice && searchId && experience?.id && !priceTrackedRef.current) {
      trackClientEvent({
        subject: `searchId:${searchId}_offerId:${experience.id}`,
        action: experience.price.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, experience?.price, experience?.id])

  return <VerticalSpacer gap={16}>
    <OverviewContainer gap={16}>
      <Heading variant="heading5">{intl.formatMessage({
        id: 'bOeoBVWVQ9y-lAWsCDaK',
        defaultMessage: 'Overview',
        description: 'Section heading for experience overview on PDP sidebar',
      })}
      </Heading>
      <VerticalSpacer gap={8}>
        {experience.copy.availability && <BodyTextBlock variant="medium" startIcon={<CalendarIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" as="span">{intl.formatMessage({
              id: 'kbt-mTaEjd-TNbvO21Op',
              defaultMessage: 'Availability:',
              description: 'Label for experience availability info on PDP sidebar',
            })}
            </BodyText> {experience.copy.availability}
          </BodyText>
        </BodyTextBlock>}

        <BodyTextBlock variant="medium" startIcon={<ClockFiveIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" as="span">{intl.formatMessage({
              id: 'sm3PJcsyxr3oSGhFWqXI',
              defaultMessage: 'Duration:',
              description: 'Form field label for experience duration info on the PDP sidebar',
            })}
            </BodyText> {durationString}
          </BodyText>
        </BodyTextBlock>

        {experience.voucher.typesAccepted.length > 0 && <BodyTextBlock variant="medium" startIcon={<MobileAndroidIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: 'oAh5GQJx50SrgeGLR2e4',
                defaultMessage: '{voucherTypes} {count, plural, one {voucher} other {vouchers}} accepted',
                description: 'Voucher types accepted for experience on PDP sidebar',
              },
              {
                voucherTypes: formatExperienceVoucherTypes(experience.voucher.typesAccepted, intl),
                count: experience.voucher.typesAccepted.length,
              },
            )}
          </BodyText>
        </BodyTextBlock>}

        {experience.copy.safetyInformation.length > 0 && <BodyTextBlock variant="medium" weight="bold" startIcon={<ShieldCheckIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'xn_H46hlsVOr-yLP5aHS',
              defaultMessage: 'Safety measures applied',
              description: 'Safety measures label for experience on PDP sidebar',
            })}
          </BodyText>
        </BodyTextBlock>}

        {experience.languages.length > 0 && <BodyTextBlock variant="medium" weight="bold" startIcon={<ChatIcon aria-hidden="true" />}>
          <BodyText variant="medium">
            <BodyText variant="medium" weight="bold" as="span">{intl.formatMessage({
              id: '6TjVOTAauLRWDqrss_Gw',
              defaultMessage: 'Language:',
              description: 'Form field label for experience language info on the PDP sidebar',
            })}
            </BodyText> {experience.languages.map(lang => lang.name).join(', ')}
          </BodyText>
        </BodyTextBlock>}

        {((isAvailable && hasPrice && (experience.cancellationPolicies.length > 0 || experience.hasInstantConfirmation)) || showGiftOption) &&
          <Group direction="vertical" gap={8}>
            <ExperienceCancellationText
              cancellationInfo={experience.copy.cancellationInfo}
              onSupportEmailClick={handleOnSupportEmailClick}
              onSupportNumberClick={handleOnSupportNumberClick}
            />
            {experience.hasInstantConfirmation &&
              <ExperienceInstantConfirmationText textSize="medium" />}
            {showGiftOption &&
              <ExperienceGiveAsGiftInfoText
                experience={experience}
                onSupportEmailClick={handleOnSupportEmailClick}
                onSupportNumberClick={handleOnSupportNumberClick}
              />}
          </Group>}
      </VerticalSpacer>

      {isAvailable && hasPrice && <>
        <HR />
        <VerticalSpacer gap={8}>
          <ExperienceAppDiscountTag experienceOffer={experience} />
          <div>
            {!showLuxPlusPrice && <PriceRowPriceWithCaption
              size="L"
              caption={fromCaption}
              price={experience.price}
              saleUnit={experience.ticketUnitLabel || fallbackSaleUnit}
            />}

            {!!showLuxPlusPrice && <>
              <PriceRowCaption>
                {intl.formatMessage({
                  id: 'eD6c8v-5jT0UxjRhHDYk',
                  defaultMessage: 'from',
                  description: 'Lowercase price caption prefix for LuxPlus pricing on experience offer page overview',
                })}
              </PriceRowCaption>
              <TightContainer>
                <PriceRowLuxPlusPrice
                  logoPlacement="trailing"
                  size="L"
                  memberPrice={experience.pricing.memberPrice}
                  saleUnit={experience.ticketUnitLabel || fallbackSaleUnit}
                />
              </TightContainer>
              <PriceRowLuxPlusNonMemberPrice nonMemberPrice={experience.price} />
            </>}

            {/* End LuxPlus Price - if available */}

            {experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="M"
              originalValue={Math.ceil(experience.grossPrice)}
              discountPercentage={experience.discount}
            />}
            <ExperiencePriceDetails experience={experience} />
          </div>
          <AgentCommissionPanel
            offer={experience}
            offerId={experience.id}
            offerTotal={experience.price ?? 0}
            retailValue={experience.grossPrice}
            memberPrice={showLuxPlusPrice ? experience.pricing.memberPrice : undefined}
          />
        </VerticalSpacer>
        <LoyaltyPointsEarn
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="estimate"
          hideStatusCredits
          offer={experience}
        />
        <TextButton kind="primary" href="#ticket-options" fit="full-width" onClick={handleCheckAvailability}>{checkAvailabilityLabel}</TextButton>

        {showGiftOption &&
          <TextButton kind="secondary" fit="full-width" onClick={handleOnGiveAsGift}>{giveAsGiftLabel}</TextButton>}

        {experiencesBNBLEnabled && !isVoucherBased && experience.productType !== 'transfer' && <BodyTextBlock
          variant="small"
          weight="bold"
          startIcon={<ClockFiveIcon />}
        >
          <FormattedMessage
            id="0mll8qUbAvDihcxKqVvN"
            defaultMessage="Buy now, choose dates later available <tooltip>Learn more</tooltip>"
            description="Buy now book later label with learn more tooltip link for experience on PDP"
            values={{
              tooltip: (chunks: React.ReactNode) => <Tooltip
                description={bnblTooltipDescription}
                onOpenChange={handleBNBLTooltipOpen}
              >
                <TextLink>{chunks}</TextLink>
              </Tooltip>,
            }}
          />
        </BodyTextBlock>}
      </>}

      {!isAvailable && <>
        <HR />
        <SoldOutPane>
          <Heading variant="heading6" align="center">{intl.formatMessage({
            id: '-7H0hcNdyBrfTsnST0o4',
            defaultMessage: 'This offer has SOLD OUT',
            description: 'Sold out heading in experience PDP sidebar for the experience',
          })}
          </Heading>
          <TextButton kind="primary" fit="full-width" to={soldOutSearchUrl} onClick={handleBrowseAllExperiencesClick}>
            {intl.formatMessage(commonMessages.browseAllExperiences)}
          </TextButton>
        </SoldOutPane>
      </>}

    </OverviewContainer>
    <OfferBNPLAd prices={experience.price} onLearnMore={handleInterestFreeLearnMoreClick} />
  </VerticalSpacer>
}

const mapStateToProps = (state: App.State) => {
  return {
    luxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(React.memo(ExperienceOfferPageOverview))
